// Curated "Day in the Life" content for high-signal careers.
// Keyed by a normalized title slug. Match via getDayInLife(career.title)
// or by family fallback.

export interface DayBlock {
  time: string;        // e.g. "8:30 AM"
  period: "morning" | "midday" | "afternoon" | "evening";
  title: string;       // short headline of the activity
  detail: string;      // 1-2 sentence description
  energy: number;      // 1-5 — how energising vs draining this block tends to be
}

export interface RealVoice {
  name: string;
  role: string;
  quote: string;
}

export interface DayInLife {
  slug: string;
  matchTitles: string[];   // alternate titles this entry serves
  family?: string;         // optional family fallback match
  /** True when this shape was derived from bundle signals, not hand-written. */
  derived?: boolean;
  tagline: string;         // 1-line vibe of the role
  vibe: string[];          // short keywords ("deep focus", "high collab")
  weekShape: {             // typical week split, percentages add to ~100
    deepWork: number;
    meetings: number;
    learning: number;
    admin: number;
  };
  blocks: DayBlock[];
  bestPart: string;
  hardestPart: string;
  voice?: RealVoice;
  tools: string[];         // common tools used day to day
}


const slugify = (s: string) =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const DATA: DayInLife[] = [
  {
    slug: "software-engineer",
    matchTitles: ["software engineer", "software developer", "backend engineer", "full stack developer", "frontend engineer"],
    family: "Technology",
    tagline: "Build the digital systems millions of people quietly rely on.",
    vibe: ["deep focus", "problem-solving", "async-friendly"],
    weekShape: { deepWork: 55, meetings: 20, learning: 15, admin: 10 },
    blocks: [
      { time: "9:00 AM", period: "morning", title: "Stand-up & inbox triage", detail: "Quick 15-min team sync, scan PR reviews and issues from overnight.", energy: 3 },
      { time: "9:45 AM", period: "morning", title: "Deep coding block", detail: "Headphones on. Ship a feature, fix a tricky bug, or refactor a gnarly module.", energy: 5 },
      { time: "1:00 PM", period: "midday", title: "Pairing or design review", detail: "Whiteboard a system with a teammate or walk through a design doc.", energy: 4 },
      { time: "3:00 PM", period: "afternoon", title: "Code reviews & ship", detail: "Review pull requests, merge, deploy, and watch dashboards to confirm health.", energy: 3 },
      { time: "5:30 PM", period: "evening", title: "Learn something new", detail: "Read a paper, try a new library, or watch a 20-min conference talk.", energy: 4 },
    ],
    bestPart: "That moment your code goes live and real users start using it.",
    hardestPart: "Debugging something that only breaks in production at 11 PM.",
    voice: { name: "Aarav, 27", role: "Software Engineer · Bangalore", quote: "Half my day is thinking, the other half is typing. Both halves matter." },
    tools: ["VS Code", "GitHub", "Slack", "Linear", "Figma", "AWS"],
  },
  {
    slug: "data-scientist",
    matchTitles: ["data scientist", "machine learning engineer", "ml engineer", "ai engineer"],
    family: "Data & Analytics",
    tagline: "Turn messy data into decisions that move the business.",
    vibe: ["analytical", "experimental", "story-driven"],
    weekShape: { deepWork: 50, meetings: 20, learning: 20, admin: 10 },
    blocks: [
      { time: "9:30 AM", period: "morning", title: "Check experiments overnight", detail: "Review model runs, A/B test dashboards, anomalies in pipelines.", energy: 3 },
      { time: "10:30 AM", period: "morning", title: "Notebook deep dive", detail: "Explore a dataset, test a hypothesis, prototype a feature for a model.", energy: 5 },
      { time: "1:30 PM", period: "midday", title: "Stakeholder readout", detail: "Translate findings into a 3-slide story for a product or business team.", energy: 4 },
      { time: "3:30 PM", period: "afternoon", title: "Model training & evaluation", detail: "Tune hyperparameters, run cross-validation, push the best model to staging.", energy: 4 },
      { time: "6:00 PM", period: "evening", title: "Paper / Kaggle / blog", detail: "Skim a new paper or write up what you learned for the team wiki.", energy: 3 },
    ],
    bestPart: "Finding a signal in the data that changes how the team thinks.",
    hardestPart: "Explaining why the 'cool' model isn't the right one for production.",
    voice: { name: "Meera, 29", role: "Senior Data Scientist · Hyderabad", quote: "The best models are the ones a non-technical exec can actually defend in a meeting." },
    tools: ["Python", "Jupyter", "SQL", "dbt", "Snowflake", "PyTorch"],
  },
  {
    slug: "product-manager",
    matchTitles: ["product manager", "associate product manager", "senior product manager"],
    family: "Product",
    tagline: "Sit at the intersection of users, business, and engineering — and make trade-offs.",
    vibe: ["high collab", "context-switching", "decisive"],
    weekShape: { deepWork: 30, meetings: 45, learning: 10, admin: 15 },
    blocks: [
      { time: "9:00 AM", period: "morning", title: "Metrics & inbox sweep", detail: "Open dashboards, scan customer tickets, prioritise the day in writing.", energy: 3 },
      { time: "10:00 AM", period: "morning", title: "User interview", detail: "30-min call with a real customer to validate or kill a hypothesis.", energy: 5 },
      { time: "12:00 PM", period: "midday", title: "Spec writing block", detail: "Quiet writing time — sharpen the next sprint's PRD with crisp acceptance criteria.", energy: 4 },
      { time: "2:30 PM", period: "afternoon", title: "Cross-functional sync", detail: "Align engineering, design, marketing on what ships and what slips.", energy: 3 },
      { time: "5:00 PM", period: "evening", title: "Roadmap & follow-ups", detail: "Update the roadmap, answer the 20 Slack threads you missed during meetings.", energy: 2 },
    ],
    bestPart: "Watching a feature you fought for change real user behaviour.",
    hardestPart: "Saying no to 9 great ideas so the 10th can actually ship.",
    voice: { name: "Rhea, 31", role: "Product Manager · Mumbai", quote: "PM is 10% writing PRDs and 90% creating clarity for everyone else." },
    tools: ["Notion", "Figma", "Linear", "Amplitude", "Slack", "Loom"],
  },
  {
    slug: "ux-designer",
    matchTitles: ["ux designer", "product designer", "ui designer", "ui ux designer"],
    family: "Design",
    tagline: "Shape how millions of people feel about a product, one pixel and prompt at a time.",
    vibe: ["visual", "empathetic", "iterative"],
    weekShape: { deepWork: 45, meetings: 25, learning: 15, admin: 15 },
    blocks: [
      { time: "9:30 AM", period: "morning", title: "Inspiration & moodboards", detail: "Scroll Mobbin, Dribbble, real apps. Collect references for the day's brief.", energy: 4 },
      { time: "10:30 AM", period: "morning", title: "Figma deep work", detail: "Push pixels: components, flows, micro-interactions. Hands on the canvas.", energy: 5 },
      { time: "1:30 PM", period: "midday", title: "User test playback", detail: "Watch 3 short usability sessions and capture what surprised you.", energy: 4 },
      { time: "3:00 PM", period: "afternoon", title: "Design critique", detail: "Walk peers through your work, take notes, defend the calls that matter.", energy: 3 },
      { time: "5:00 PM", period: "evening", title: "Design system hygiene", detail: "Update tokens, document a new component, hand off to engineering.", energy: 3 },
    ],
    bestPart: "A user describing your screen exactly the way you intended it to feel.",
    hardestPart: "Killing a design you love because the data says it confuses people.",
    voice: { name: "Kabir, 26", role: "Product Designer · Pune", quote: "Good design is invisible. That's also why explaining your job at dinner is so hard." },
    tools: ["Figma", "FigJam", "Maze", "Notion", "Principle", "Mobbin"],
  },
  {
    slug: "doctor",
    matchTitles: ["doctor", "physician", "medical doctor", "general practitioner"],
    family: "Healthcare",
    tagline: "Use deep knowledge and steady hands to give people back their normal life.",
    vibe: ["high stakes", "people-first", "always learning"],
    weekShape: { deepWork: 20, meetings: 55, learning: 15, admin: 10 },
    blocks: [
      { time: "7:30 AM", period: "morning", title: "Ward rounds", detail: "Review every admitted patient with the team, adjust treatment plans.", energy: 4 },
      { time: "10:00 AM", period: "morning", title: "OPD consultations", detail: "Back-to-back patients — listen, examine, decide, document, repeat.", energy: 5 },
      { time: "1:30 PM", period: "midday", title: "Quick lunch + lab results", detail: "Eat in 15 min, then triage incoming reports and follow up the urgent ones.", energy: 3 },
      { time: "3:00 PM", period: "afternoon", title: "Procedures or surgeries", detail: "Scrubs on. Focus mode. A small window where nothing else exists.", energy: 5 },
      { time: "7:00 PM", period: "evening", title: "Notes, journals, on-call", detail: "Finish patient notes, read up on one tricky case, hand over to the night team.", energy: 2 },
    ],
    bestPart: "A patient walking out smiling who came in scared.",
    hardestPart: "The calls you can't fix, no matter how hard you try.",
    voice: { name: "Dr. Anika, 32", role: "Internal Medicine · Delhi", quote: "Medicine is mostly listening. The diagnosis usually arrives if you let the patient finish their sentence." },
    tools: ["Stethoscope", "EMR", "UpToDate", "PubMed", "WhatsApp (referrals)"],
  },
  {
    slug: "teacher",
    matchTitles: ["teacher", "school teacher", "educator", "high school teacher"],
    family: "Education",
    tagline: "Hold 30 different brains in one room and make them all curious at the same time.",
    vibe: ["performative", "structured", "deeply human"],
    weekShape: { deepWork: 25, meetings: 50, learning: 15, admin: 10 },
    blocks: [
      { time: "7:45 AM", period: "morning", title: "Assembly & setup", detail: "Greet students, prep the board, queue today's slides and materials.", energy: 4 },
      { time: "8:30 AM", period: "morning", title: "Back-to-back classes", detail: "Teach, perform, watch faces, adjust pace, drop in a story when energy dips.", energy: 5 },
      { time: "12:30 PM", period: "midday", title: "Lunch + 1:1 student chats", detail: "Quick check-ins with kids who looked off this morning.", energy: 4 },
      { time: "2:00 PM", period: "afternoon", title: "Grading & feedback", detail: "Mark papers, leave comments that actually help, not just tick marks.", energy: 3 },
      { time: "4:00 PM", period: "evening", title: "Lesson planning", detail: "Design tomorrow's hook — the 90 seconds that decides whether kids lean in.", energy: 4 },
    ],
    bestPart: "Years later, an ex-student writing to say one of your sentences stuck.",
    hardestPart: "Reaching the one kid who has decided not to be reached.",
    voice: { name: "Priya, 34", role: "Physics Teacher · Chennai", quote: "Teaching is the only job where the curriculum is fixed but the audience changes every 45 minutes." },
    tools: ["Whiteboard", "Google Classroom", "Slides", "Khan Academy", "Notebook"],
  },
  {
    slug: "chartered-accountant",
    matchTitles: ["chartered accountant", "accountant", "ca", "auditor", "tax consultant"],
    family: "Finance",
    tagline: "Be the person every founder, family, and CFO trusts with the real numbers.",
    vibe: ["precise", "advisory", "seasonal sprints"],
    weekShape: { deepWork: 50, meetings: 25, learning: 15, admin: 10 },
    blocks: [
      { time: "9:30 AM", period: "morning", title: "Client inbox & priorities", detail: "Filter urgent filings, GST notices, audit queries from overnight.", energy: 3 },
      { time: "10:30 AM", period: "morning", title: "Statements & reconciliations", detail: "Deep number work — ledgers, variances, the 'why' behind every line.", energy: 5 },
      { time: "1:30 PM", period: "midday", title: "Client advisory call", detail: "Translate spreadsheets into clear next steps a founder can act on.", energy: 4 },
      { time: "3:30 PM", period: "afternoon", title: "Audit team review", detail: "Walk through workpapers with juniors, sharpen sampling and conclusions.", energy: 3 },
      { time: "6:00 PM", period: "evening", title: "Compliance & filings", detail: "File a return, respond to a tax notice, log time, close the loop.", energy: 3 },
    ],
    bestPart: "Spotting the one entry that saves the client lakhs — quietly.",
    hardestPart: "March and September. Enough said.",
    voice: { name: "Rohan, 30", role: "CA · Mumbai", quote: "Half my job is accounting. The other half is being a calm adult in someone else's panic." },
    tools: ["Tally", "Zoho Books", "Excel", "ClearTax", "MCA portal"],
  },
  {
    slug: "marketing-manager",
    matchTitles: ["marketing manager", "growth manager", "brand manager", "digital marketing manager"],
    family: "Marketing",
    tagline: "Tell a story so well that the right people show up — and stay.",
    vibe: ["creative", "data-led", "fast-moving"],
    weekShape: { deepWork: 35, meetings: 35, learning: 15, admin: 15 },
    blocks: [
      { time: "9:30 AM", period: "morning", title: "Campaign dashboard scan", detail: "Open analytics: CPA, CTR, retention, organic spikes. Where do we lean in?", energy: 4 },
      { time: "10:30 AM", period: "morning", title: "Brief & copy block", detail: "Write a campaign brief, sharpen hooks, ship 5 ad variants for testing.", energy: 5 },
      { time: "1:00 PM", period: "midday", title: "Sync with creative & sales", detail: "Align assets, talking points, landing pages so the funnel doesn't leak.", energy: 4 },
      { time: "3:00 PM", period: "afternoon", title: "Customer & community", detail: "Read DMs, replies, reviews. Steal the actual words customers use.", energy: 4 },
      { time: "5:00 PM", period: "evening", title: "Plan & measure", detail: "Update the roadmap, set the next experiment, post a learnings note.", energy: 3 },
    ],
    bestPart: "A line you wrote becoming the thing customers repeat back to you.",
    hardestPart: "Defending 'brand' to people who only believe in this week's CAC.",
    voice: { name: "Tara, 28", role: "Growth Manager · Bangalore", quote: "If your ad sounds like an ad, you've already lost. Sound like a friend with a recommendation." },
    tools: ["Google Analytics", "Meta Ads", "Notion", "Figma", "Mixpanel", "Canva"],
  },
  {
    slug: "civil-services-officer",
    matchTitles: ["ias officer", "ips officer", "civil services officer", "ifs officer", "ias", "ips"],
    family: "Public Service",
    tagline: "Sit in the room where decisions affect entire districts — and own them.",
    vibe: ["high responsibility", "varied", "people-heavy"],
    weekShape: { deepWork: 25, meetings: 50, learning: 10, admin: 15 },
    blocks: [
      { time: "8:00 AM", period: "morning", title: "Public hearings (Janata Darbar)", detail: "Hear citizens directly — grievances, requests, complaints. Decide on the spot when possible.", energy: 5 },
      { time: "10:30 AM", period: "morning", title: "Department reviews", detail: "Health, education, revenue — quick stand-ups, blockers, deadlines.", energy: 4 },
      { time: "1:00 PM", period: "midday", title: "Field visit", detail: "Drive out to a school, PHC, or project site. See the ground truth, not the report.", energy: 5 },
      { time: "4:00 PM", period: "afternoon", title: "Files & approvals", detail: "Sign tenders, scheme files, notings — every signature has weight.", energy: 3 },
      { time: "7:30 PM", period: "evening", title: "Calls with state HQ", detail: "Brief the secretary, line up tomorrow, read the night's reports.", energy: 2 },
    ],
    bestPart: "Driving past a road, school, or clinic that exists because you pushed it through.",
    hardestPart: "Knowing every decision will make someone unhappy — and deciding anyway.",
    voice: { name: "Vivek, 33", role: "Sub-Collector · Tamil Nadu", quote: "The job is part judge, part project manager, part counsellor. Some days, all three before lunch." },
    tools: ["File notings", "WhatsApp groups", "GIS dashboards", "PRAGATI"],
  },
  {
    slug: "lawyer",
    matchTitles: ["lawyer", "advocate", "corporate lawyer", "litigator"],
    family: "Legal",
    tagline: "Argue, draft, and negotiate for people who can't speak the language of law.",
    vibe: ["argumentative", "research-heavy", "high stakes"],
    weekShape: { deepWork: 45, meetings: 30, learning: 15, admin: 10 },
    blocks: [
      { time: "8:30 AM", period: "morning", title: "Court prep", detail: "Reread the brief, mark the strongest 2 authorities, anticipate the bench.", energy: 5 },
      { time: "10:30 AM", period: "morning", title: "Court appearance / mediation", detail: "Argue, listen, adapt. Sometimes 2 minutes of fame, sometimes 2 hours.", energy: 5 },
      { time: "1:30 PM", period: "midday", title: "Client meeting", detail: "Explain the law in plain language, set expectations honestly.", energy: 4 },
      { time: "3:30 PM", period: "afternoon", title: "Drafting block", detail: "Contracts, petitions, replies — pick every word like it's evidence.", energy: 4 },
      { time: "6:30 PM", period: "evening", title: "Research & next-day prep", detail: "Pull precedents, build the bundle, brief the junior.", energy: 3 },
    ],
    bestPart: "A judgment that changes the way a clause is read across the country.",
    hardestPart: "Adjournments. So many adjournments.",
    voice: { name: "Sana, 29", role: "Litigator · Delhi High Court", quote: "Most of law is writing. The shouting in court is just the 10% you see on TV." },
    tools: ["SCC Online", "Manupatra", "LiveLaw", "Word", "OneDrive"],
  },
  {
    slug: "entrepreneur",
    matchTitles: ["entrepreneur", "founder", "startup founder", "co-founder", "ceo"],
    family: "Business",
    tagline: "Wear every hat for a few years so the company can wear none forever.",
    vibe: ["chaotic", "high agency", "ownership"],
    weekShape: { deepWork: 30, meetings: 45, learning: 15, admin: 10 },
    blocks: [
      { time: "7:30 AM", period: "morning", title: "Top 3 priorities", detail: "Write the 3 things that, if done today, make the week a win.", energy: 5 },
      { time: "9:00 AM", period: "morning", title: "Customer & sales calls", detail: "Talk to users, close deals, hear the actual words people use to describe the problem.", energy: 5 },
      { time: "1:00 PM", period: "midday", title: "Team 1:1s", detail: "Unblock people, hire well, fire kindly, repeat the vision until you're sick of it.", energy: 4 },
      { time: "4:00 PM", period: "afternoon", title: "Builder mode", detail: "Write the deck, build the model, ship the landing page, no one else can.", energy: 4 },
      { time: "8:00 PM", period: "evening", title: "Investor / mentor calls", detail: "Update one believer, ask one hard question, sleep on the answer.", energy: 3 },
    ],
    bestPart: "Watching a stranger pay you for something that didn't exist last year.",
    hardestPart: "Some weeks, you carry the morale of the whole team — even your own.",
    voice: { name: "Karan, 30", role: "Founder · SaaS startup", quote: "The job is to keep the company alive long enough to get lucky — and earn the luck." },
    tools: ["Notion", "Slack", "Linear", "Stripe", "Cal.com", "WhatsApp"],
  },
  {
    slug: "psychologist",
    matchTitles: ["psychologist", "clinical psychologist", "counselor", "counsellor", "therapist"],
    family: "Healthcare",
    tagline: "Hold space for the things people can't tell anyone else.",
    vibe: ["deeply present", "boundaried", "growth-oriented"],
    weekShape: { deepWork: 35, meetings: 50, learning: 15, admin: 0 },
    blocks: [
      { time: "9:00 AM", period: "morning", title: "Intake & prep", detail: "Reread case notes, mentally arrive for each client before they walk in.", energy: 4 },
      { time: "10:00 AM", period: "morning", title: "Client sessions (back to back)", detail: "Listen, reflect, gently challenge, sit with silence when it matters.", energy: 5 },
      { time: "1:30 PM", period: "midday", title: "Reset ritual", detail: "Walk, breathe, journal a line. You can't pour from an empty cup — literally.", energy: 3 },
      { time: "2:30 PM", period: "afternoon", title: "More sessions + crisis call", detail: "Some days, one urgent call reshapes the whole afternoon.", energy: 4 },
      { time: "6:00 PM", period: "evening", title: "Notes, supervision, learning", detail: "Write up sessions, debrief tough cases with a supervisor, read a chapter.", energy: 3 },
    ],
    bestPart: "When a client says, 'I noticed I did it differently this week.'",
    hardestPart: "Carrying other people's stories home — and learning how not to.",
    voice: { name: "Dr. Ira, 31", role: "Clinical Psychologist · Bangalore", quote: "My job isn't to fix people. It's to sit with them until they can hear themselves." },
    tools: ["DSM-5", "Notion (notes)", "Calendly", "Supervision group"],
  },
];

const BY_SLUG = new Map(DATA.map((d) => [d.slug, d] as const));
const BY_TITLE = new Map<string, DayInLife>();
for (const d of DATA) {
  for (const t of d.matchTitles) BY_TITLE.set(slugify(t), d);
  BY_TITLE.set(d.slug, d);
}

/**
 * Exact matches only.
 *
 * These personas are hand-written interviews about ONE role. A substring or
 * family fallback used to hand another role's persona to a career it was never
 * written about — "Generative AI Engineer" collected the data-scientist entry,
 * complete with a named practitioner who has never done that job. That is
 * misinformation about a real career, so there is no fuzzy path here.
 *
 * When there's no exact match, callers fall back to the derived day shape in
 * `careerCopy.deriveDayShape`, never to someone else's persona.
 */
export function getDayInLife(title?: string | null): DayInLife | undefined {
  if (!title) return undefined;
  return BY_TITLE.get(slugify(title));
}

export function hasDayInLife(title?: string | null): boolean {
  return !!getDayInLife(title);
}

export const ALL_DAY_IN_LIFE = DATA;
export { BY_SLUG as DAY_IN_LIFE_BY_SLUG };

